<template>
  <div class="w-full flex flex-col mx-auto p-4 mt-2 space-y-1">

  <!-- All resolved -->
    <template v-if="allResolved">
      <div class="w-full whitespace-normal">
        All {{ creditLines.length }} credit {{ creditLines.length === 1 ? 'line has' : 'lines have' }} been assigned a target.
      </div>
      <div class="w-full flex items-center justify-between border-t pt-3">
        <button type="button" class="bn-text" @click="$emit('back')">
          <ArrowSmLeftIcon />
          <div>Back to Estimate</div>
        </button>
        <button type="button" class="bn-solid-green" @click="$emit('confirm')">
          Continue
        </button>
      </div>
    </template>

  <!-- No candidates — blocked -->
    <template v-else-if="isBlocked">
      <div class="w-full flex items-center flex-wrap border-b p-1">
        <div class="text-sky w-auto mr-2 min-w-32 text-left">{{ capitalizeString(currentLine.partNumber) }}</div>
        <div class="flex-grow text-left whitespace-normal truncate text-red">
          Hours: {{ currentLine.hours }} &middot; Revenue: {{ showAsUSCurrency(parseServiceRevenue(currentLine.revenue)) }}
        </div>
      </div>
      <div class="w-full whitespace-normal chip-red p-2">{{ emptyReasonMessage }}</div>
      <div class="w-full border-t pt-3">
        <button type="button" class="bn-text" @click="$emit('back')">
          <ArrowSmLeftIcon />
          <div>Back to Estimate</div>
        </button>
      </div>
    </template>

  <!-- Step through manual lines -->
    <template v-else>
      <div class="w-full text-left mr-2" v-if="manualLines.length > 1">Credit {{ currentIndex + 1 }} of {{ manualLines.length }}</div>
      <div class="w-full flex items-center flex-wrap border-b p-1">
        <div class="text-sky w-auto mr-2 min-w-32 text-left">{{ capitalizeString(currentLine.partNumber) }}</div>
        <div class="flex-grow text-left whitespace-normal truncate text-red">
          Hours: {{ currentLine.hours }} &middot; Revenue: {{ showAsUSCurrency(parseServiceRevenue(currentLine.revenue)) }}
        </div>
      </div>
      <div class="w-full text-left whitespace-normal truncate p-1 text-indigo">Apply credit to:</div>
      <div v-for="candidate in currentCandidates.candidates" :key="candidateKey(candidate)" class="w-full p-1">
        <div class="w-full flex items-center flex-wrap">
          <div class="flex-grow flex items-center flex-wrap">
            <div class="mr-2 min-w-24 whitespace-normal truncate text-left">{{ candidateLabel(candidate) }}</div>
            <div>{{ candidate.hours }} hrs &middot; {{ showAsUSCurrency(parseServiceRevenue(candidate.revenue)) }}</div>
          </div>
          <div class="p-1">
            <ButtonWithConfirm :buttonClass="'bn-text'" :buttonText="'Select'" @confirmed="selectCandidate(candidate)" />
          </div>
        </div>

      </div>
      <div class="w-full border-t pt-3">
        <button type="button" class="bn-text" @click="$emit('back')">
          <ArrowSmLeftIcon />
          <div>Back to Estimate</div>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { ArrowSmLeftIcon } from '@heroicons/vue/solid'
import { buildCreditAbsorptionDetail, parseServiceRevenue } from '@/shared/changeOrderCreditTargets'
import ButtonWithConfirm from '@/components/ui/ButtonWithConfirm.vue' 
import {showAsUSCurrency,capitalizeString} from '@/shared'

const EMPTY_REASON_MESSAGES = {
  no_positive_bucket: 'No matching part # found with positive hours and revenue on the contract or any prior change order.',
  exhausted_by_other_credits: 'All available capacity for this part # has been consumed by other credits in this estimate.',
  credit_exceeds_remaining: 'This credit is larger than the remaining capacity in the contract or any change order.'
}

export default {
  name: 'ChangeOrderCreateReviewPanel',
  components: { ArrowSmLeftIcon,ButtonWithConfirm },
  props: {
    creditLines: { type: Array, default: () => [] },
    masterServices: { type: Array, default: () => [] },
    legacyChangeOrders: { type: Array, default: () => [] }
  },
  emits: ['back', 'confirm'],
  setup(props) {
    const currentIndex = ref(0)

    const manualLines = computed(() =>
      props.creditLines.filter(l => !l.creditAllocationAuto)
    )

    const currentLine = computed(() => manualLines.value[currentIndex.value] ?? null)

    const currentCandidates = computed(() => {
      if (!currentLine.value) return { candidates: [], emptyReason: null }
      return buildCreditAbsorptionDetail(
        currentLine.value.partNumber,
        props.masterServices,
        props.legacyChangeOrders,
        {
          draftCreditLines: props.creditLines,
          excludeLocalId: currentLine.value.localId,
          creditLine: currentLine.value
        }
      )
    })

    const isBlocked = computed(() =>
      currentLine.value !== null && currentCandidates.value.emptyReason !== null
    )

    const allResolved = computed(() => currentIndex.value >= manualLines.value.length)

    const emptyReasonMessage = computed(() =>
      EMPTY_REASON_MESSAGES[currentCandidates.value.emptyReason] ?? 'No allocation target available.'
    )

    const candidateLabel = (c) => {
      if (c.scope === 'master') return c.document === 'contract' ? 'Contract' : c.document
      return `CO ${c.companyProjectNumber ?? c.projectId}`
    }

    const candidateKey = (c) =>
      c.scope === 'master' ? `master-${c.document}` : `legacy-${c.projectId}-${c.document}`

    const selectCandidate = (candidate) => {
      if (!currentLine.value) return
      if (candidate.scope === 'master') {
        currentLine.value.creditApplyTarget = { scope: 'master', document: candidate.document }
      } else {
        currentLine.value.creditApplyTarget = { scope: 'legacy', projectId: candidate.projectId, document: candidate.document }
      }
      delete currentLine.value.creditAllocationAuto
      currentIndex.value++
    }

    return {
      currentIndex,
      manualLines,
      currentLine,
      currentCandidates,
      isBlocked,
      allResolved,
      emptyReasonMessage,
      candidateLabel,
      candidateKey,
      selectCandidate,
      showAsUSCurrency,
      parseServiceRevenue,
      capitalizeString
    }
  }
}
</script>
